import { faEye, faMagnifyingGlass } from "@fortawesome/pro-light-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { Icon } from "src/base-components/Icon";
import {
  Cell,
  ColumnVisibilityDropdown,
  TableComp,
} from "src/base-components/Table";
import { EmptyState } from "src/design-system/EmptyState";
import { EntitySchemaEditorSidebar } from "src/entities/EntitySchemaEditorSidebar";
import {
  TypeIcon,
  renderValue,
} from "src/entities/entityView/EntityDetailsSidePane";
import { convertFilters } from "src/entities/entityView/utils";
import { getOrderedPropertiesByDisplayName } from "src/entities/entityView/utils";
import {
  ENTITIES_PAGE_SIZE,
  EntityResource,
  EntitySchemaResource,
  useEntities,
} from "src/entities/queries";
import { useEnvironment } from "src/eventsCatalogue/useEnvironment";
import { WhitePane } from "src/jobs/common/WhitePane";
import { FiltersDropdown } from "src/manualReview/Table";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { getUrlToEntityDetailPage } from "src/router/urls";

type EntitiesTableProps = {
  baseUrl: string;
  entitySchema: EntitySchemaResource;
  isEditing: boolean;
  onSidebarClose: () => void;
  idFilter: Record<string, string[]>;
  schemasLoading: boolean;
};

const onSelect = (
  key: string,
  newValue: string[],
  setFilters: React.Dispatch<React.SetStateAction<Record<string, string[]>>>,
) => {
  setFilters((prev) => {
    if (newValue.length === 0) {
      const { [key]: _, ...rest } = prev;
      return rest;
    }

    return {
      ...prev,
      [key]: newValue,
    };
  });
};

export const EntitiesTable: React.FC<EntitiesTableProps> = ({
  baseUrl,
  entitySchema,
  isEditing,
  onSidebarClose,
  idFilter,
  schemasLoading,
}) => {
  const [env] = useEnvironment();
  const [filters, setFilters] = useState<Record<string, string[]>>({});

  const { data, isLoading, fetchNextPage } = useEntities({
    schema: entitySchema._id,
    env,
    baseUrl,
    filters: convertFilters(filters),
    limit: ENTITIES_PAGE_SIZE,
  });
  const entities = useMemo(
    () =>
      data?.pages.flatMap((page) => page.entities as EntityResource[]) ?? [],
    [data],
  );

  const { orgId, workspace } = useWorkspaceContext();
  const navigate = useNavigate();

  const showEmptyState = !isLoading && (!data || entities.length === 0);
  const emptyStateDescription =
    "Make sure your search starts with the exact property value or includes it in full, or try searching a different entity";
  const emptyStateHeadline = `No ${entitySchema._display_name_plural.toLowerCase()} found`;

  useEffect(() => {
    onSelect("_id", idFilter._id ?? [], setFilters);
  }, [idFilter]);

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<EntityResource>();
    const orderedProperties = getOrderedPropertiesByDisplayName(entitySchema);

    const entityColumns = orderedProperties.map((key) => {
      const value = entitySchema.properties[key];
      const isEnum = value._type === "enum";

      return columnHelper.accessor(`properties.${key}`, {
        meta: {
          displayName: value._display_name,
        },
        header: () => (
          <div className="flex items-center gap-1 pt-4">
            <TypeIcon cardinality={value._cardinality} type={value._type} />
            <p className="whitespace-nowrap pr-2">{value._display_name}</p>
            {isEnum && value._values && value._values.length > 0 && (
              <span className="pr-2">
                <FiltersDropdown
                  description={`Filter by ${value._display_name}`}
                  elements={value._values.map((v) => ({
                    key: v._internal_name,
                    value: v._internal_name,
                  }))}
                  isFiltering={false}
                  multiple={true}
                  placement="bottomLeft"
                  selected={filters[key]}
                  itemsPortal
                  onResetRequest={() => {}}
                  onSelect={(values: string[]) =>
                    onSelect(key, values, setFilters)
                  }
                />
              </span>
            )}
          </div>
        ),
        cell: (info) => (
          <Cell info={info}>
            {renderValue(
              value._type,
              info.row.original.properties[key],
              value._rel_schema,
              true,
            )}
          </Cell>
        ),
      });
    });

    const detailsColumn = columnHelper.display({
      id: "fields.options",
      header: (info) => (
        <div className="flex justify-end pr-1.5 pt-4">
          <ColumnVisibilityDropdown min={3} table={info.table} />
        </div>
      ),
      size: 54,
      cell: (info) => (
        <Cell className="items-center justify-end" info={info}>
          <Icon
            color="text-gray-500 opacity-0 group-hover:opacity-100"
            icon={faEye}
            size="xs"
          />
        </Cell>
      ),
    });

    return [...entityColumns, detailsColumn];
  }, [entitySchema, filters, setFilters]);

  return (
    <FloatingWindowsProvider>
      <div className="flex">
        <div className="m-5 max-h-screen w-full overflow-hidden">
          <WhitePane>
            {showEmptyState ? (
              <div className="h-150">
                <EmptyState
                  description={emptyStateDescription}
                  headline={emptyStateHeadline}
                  icon={faMagnifyingGlass}
                />
              </div>
            ) : (
              <TableComp
                columns={columns}
                data={entities ?? []}
                frameClassName="w-full p-4 pt-0"
                isLoading={isLoading || schemasLoading}
                rowClassName="hover:bg-gray-50 cursor-pointer"
                rowPropsGetter={(row) => ({
                  onClick: () =>
                    navigate(
                      getUrlToEntityDetailPage(
                        orgId,
                        workspace.id,
                        row.original._schema,
                        row.original._id,
                      ),
                    ),
                })}
                variant="compact"
                onScrolledToTheBottom={fetchNextPage}
              />
            )}
          </WhitePane>
        </div>
        {isEditing && (
          <>
            <div className="w-150 flex-shrink-0" />
            <div className="fixed right-0 z-10 h-[calc(100vh-100px)]">
              <EntitySchemaEditorSidebar
                schema={entitySchema}
                onClose={onSidebarClose}
              />
            </div>
          </>
        )}
      </div>
    </FloatingWindowsProvider>
  );
};
